import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { AuthSession } from "../../../auth/keycloak.js";
import { useElementBlueprints } from "./useElementBlueprints.js";

const BLUEPRINT_TEXTS = {
  de: () => import("../../../locale/messages/blueprints/de.json"),
  en: () => import("../../../locale/messages/blueprints/en.json"),
};

type ElementInfo = {
  /** The blueprint's description text; `null` when it has none or it cannot be known here, `undefined` while loading. */
  descriptionOf: (element: { blueprintId: number | null; blueprintName: string | null }) => string | null | undefined;
};

/** Without a provider (a card rendered on its own) no description is known. */
const ElementInfoContext = createContext<ElementInfo>({ descriptionOf: () => null });

export const useElementInfo = () => useContext(ElementInfoContext);

/**
 * What an element's blueprint is for. The text resolves like the legacy's picker (`ARLMS_BlueprintSelection.js`): the locale
 * text by blueprint name first, then the blueprint's own description. Most main elements have only the locale text.
 */
export function ElementInfoProvider({
  session,
  manualId,
  catalogueAvailable,
  children,
}: {
  session: AuthSession | null;
  manualId: number;
  /** The catalogue is refused on a linked copy and has no use on a manual without AR elements, so it is not asked for there. */
  catalogueAvailable: boolean;
  children: ReactNode;
}) {
  const { i18n } = useTranslation();
  const catalog = useElementBlueprints(session, manualId, catalogueAvailable);
  const language = i18n.language.startsWith("de") ? "de" : "en";
  const localeTexts = useQuery({
    queryKey: ["blueprint-texts", language],
    staleTime: Infinity,
    queryFn: async () => (await BLUEPRINT_TEXTS[language]()).default.blueprintDescriptions as Record<string, string | undefined>,
  });
  const value = useMemo<ElementInfo>(() => {
    const descriptions = new Map<number, string | null>();
    for (const category of catalog.data?.categories ?? []) {
      for (const blueprint of category.blueprints) {
        // A sensor tile repeats its blueprint's id and carries its DEVICE's text, which would land on other devices' elements.
        if (blueprint.sensor) continue;
        if (!descriptions.get(blueprint.id)) descriptions.set(blueprint.id, blueprint.description);
      }
    }
    return {
      descriptionOf: ({ blueprintId, blueprintName }) => {
        const localeText = blueprintName === null ? undefined : localeTexts.data?.[blueprintName];
        if (localeText) return localeText;
        if (localeTexts.isPending) return undefined;
        if (!catalogueAvailable || catalog.isError) return null;
        if (catalog.isPending) return undefined;
        return blueprintId === null ? null : (descriptions.get(blueprintId) ?? null);
      },
    };
  }, [catalogueAvailable, catalog.data, catalog.isPending, catalog.isError, localeTexts.data, localeTexts.isPending]);
  return <ElementInfoContext.Provider value={value}>{children}</ElementInfoContext.Provider>;
}
